import type { CSSProperties } from 'react';
import { Card, Descriptions, Space, Tag, Typography } from 'antd';
import { EyebrowLabel } from '@viq-admin/ui';
import { displayName } from './labels';
import { toLinkedinHref } from './linkedinUrl';
import type { LeadDetail } from './types';

interface Props {
  detail: LeadDetail;
}

const STATUS_COLOR: Record<string, string> = {
  new: 'blue',
  active: 'green',
  closed: 'default',
  archived: 'default',
};

const TEMP_COLOR: Record<string, string> = {
  hot: 'red',
  warm: 'orange',
  cold: 'cyan',
};

const CAPTURE_COLOR: Record<string, string> = {
  voice: 'purple',
  card: 'green',
  badge: 'blue',
};

/**
 * antd merges a partial `column` map over its OWN defaults
 * ({ xxl: 3, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }), so the previous
 * `{ xs: 1, sm: 2 }` was a no-op — every breakpoint from md up still resolved
 * to THREE columns. On any desktop window the contact block was a 3-column
 * grid inside a narrow card, which is what squeezed a normal-length address
 * onto two lines. Name every breakpoint so the grid is genuinely capped at two.
 */
const CONTACT_COLUMNS = { xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 } as const;

/**
 * An email address reads as one token, but antd's Descriptions content defaults
 * to `word-break: break-word` — which split a real lead's address mid-domain
 * ("…donfran / klinauto.com") once it outgrew its half-width column. The Email
 * row is widened to the full grid below; this keeps the address on one line
 * regardless of width, and ellipses an unusually long one rather than wrapping
 * or stretching the card. The full value stays reachable via the `title`
 * tooltip and the mailto href.
 *
 * `display: inline-block` + `max-width` are what make `text-overflow` apply at
 * all — antd's Typography.Link renders a plain inline <a>, and ellipsis is
 * ignored on inline boxes.
 */
const SINGLE_LINE_VALUE: CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'bottom',
  whiteSpace: 'nowrap',
};

export function HeaderCard({ detail }: Props) {
  const { person, capturedByName, conferenceName } = detail;

  return (
    <Card size="small">
      <EyebrowLabel>Person</EyebrowLabel>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {person.displayName || '—'}
          </Typography.Title>
          {person.title && (
            <Typography.Text type="secondary">{person.title}</Typography.Text>
          )}
          {person.company && (
            <>
              {person.title && ' · '}
              <Typography.Text type="secondary">{person.company}</Typography.Text>
            </>
          )}
        </div>

        <Space wrap>
          <Tag color={STATUS_COLOR[person.status] ?? 'default'}>
            {displayName('status', person.status) || person.status || '—'}
          </Tag>
          <Tag color={TEMP_COLOR[person.temperature] ?? 'default'}>
            {displayName('temp', person.temperature) || person.temperature || '—'}
          </Tag>
          <Tag color={CAPTURE_COLOR[person.captureMethod] ?? 'default'}>
            {displayName('capture', person.captureMethod) || person.captureMethod || '—'}
          </Tag>
        </Space>

        <Descriptions column={CONTACT_COLUMNS} size="small">
          {person.email && (
            // Full row at every breakpoint — the widest the card can offer.
            // Reuses the column map so span and column cannot drift apart.
            <Descriptions.Item label="Email" span={CONTACT_COLUMNS}>
              <Typography.Link
                href={`mailto:${person.email}`}
                style={SINGLE_LINE_VALUE}
                title={person.email}
              >
                {person.email}
              </Typography.Link>
            </Descriptions.Item>
          )}
          {person.phone && (
            <Descriptions.Item label="Phone">
              <Typography.Link href={`tel:${person.phone}`}>
                {person.phone}
              </Typography.Link>
            </Descriptions.Item>
          )}
          {/*
            Always rendered, unlike Email/Phone: admins asked where the LinkedIn
            field was (viq-admin#30) because a lead without one showed no row at all.
            The dash tells them the field exists and is empty; Edit fills it.
          */}
          <Descriptions.Item label="LinkedIn">
            {person.linkedinUrl.trim() ? (
              <Typography.Link
                href={toLinkedinHref(person.linkedinUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={SINGLE_LINE_VALUE}
                title={person.linkedinUrl}
              >
                {person.linkedinUrl}
              </Typography.Link>
            ) : (
              '—'
            )}
          </Descriptions.Item>
        </Descriptions>

        <Typography.Text type="secondary">
          Captured by {capturedByName || '—'} at {conferenceName || '—'}
        </Typography.Text>
      </Space>
    </Card>
  );
}
